import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LoginScreen.css";
import SpinnerOverlay from "../components/spinnerOverlay/SpinnerOverlay";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-hot-toast";
import { useSocialAuth } from "../hooks/useSocialAuth";
import { consumePostAuthRedirect } from "../utils/postAuthRedirect";

// ─── Utility ────────────────────────────────────────────────────────────────
/**
 * Normalise any Nigerian phone variant to the 234XXXXXXXXXX format.
 *   08012345678  →  2348012345678
 *   +2348012345678  →  2348012345678
 *   2348012345678   →  2348012345678
 * Non-phone strings (emails) are returned unchanged.
 */
const normalizeIdentifier = (value) => {
  const trimmed = value.trim();
  // If it contains @ it is an email – leave it alone
  if (trimmed.includes("@")) return trimmed;

  // Strip all spaces and leading +
  let digits = trimmed.replace(/\s+/g, "").replace(/^\+/, "");

  // 0XXXXXXXXXX  →  234XXXXXXXXXX
  if (digits.startsWith("0")) {
    digits = "234" + digits.slice(1);
  }

  // If still missing the country code, prepend it
  if (!digits.startsWith("234")) {
    digits = "234" + digits;
  }

  return digits;
};

// Mask the middle part of a phone for display: 2348012345678 → 234801***5678
const maskPhone = (phone) => {
  if (!phone) return "";
  if (phone.includes("@")) {
    const [local, domain] = phone.split("@");
    return local.slice(0, 3) + "***@" + domain;
  }
  return phone.slice(0, 6) + "***" + phone.slice(-4);
};

// ─── Component ──────────────────────────────────────────────────────────────
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();

  // ── Step 1 state ──
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  // ── Step 2 (OTP) state ──
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [maskedContact, setMaskedContact] = useState("");
  // We temporarily hold the normalised identifier so the OTP call can use it
  const pendingIdentifierRef = useRef("");

  // ── Slideshow state ──
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { handleSocialAuth } = useSocialAuth();

  const imageTexts = [
    {
      title: "Personalized Learning: ",
      subtitle: "Get the Best Tutor for You or Your Child.",
    },
    { title: "Share Your Knowledge: ", subtitle: "Become a Tutor Today!" },
    {
      title: "Get Top-Notch Tutoring: ",
      subtitle: "For Kids, Adults, and Everyone In-Between.",
    },
    { title: "Share Your Knowledge: ", subtitle: "Become a Tutor Today!" },
  ];

  const images = [
    "../images/work-educational.jpg",
    "../images/tutoring.jpg",
    "../images/teaching-kid-write.jpg",
    "../images/study-group2.jpg",
  ];

  // ── Effects ──
  useEffect(() => {
    if (location.state?.identifier) setIdentifier(location.state.identifier);
    if (location.state?.message) toast.success(location.state.message);
  }, [location.state]);

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentImageIndex((prev) => (prev + 1) % images.length),
      3000,
    );
    return () => clearInterval(interval);
  }, [images.length]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Validation ──
  const isPasswordValid = (pass) =>
    pass.length >= 8 &&
    /[A-Z]/.test(pass) &&
    /[a-z]/.test(pass) &&
    /\d/.test(pass) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(pass);

  const validateForm = () => {
    const tempErrors = {};
    if (!identifier.trim())
      tempErrors.identifier = "Email or phone number is required";
    if (!password) {
      tempErrors.password = "Password is required";
    } else if (!isPasswordValid(password)) {
      tempErrors.password = "Password must meet all criteria";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // ── Step 1: Submit credentials ──

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    const normalised = normalizeIdentifier(identifier);
    pendingIdentifierRef.current = normalised;

    setIsLoading(true);
    setServerError("");

    try {
      const res = await api.post("/users/login", {
        identifier: normalised,
        password,
      });

      // ✅ SUCCESS RESPONSE WITH OTP
      if (res.data?.otpRequired) {
        setMaskedContact(res.data.maskedContact || maskPhone(normalised));

        setResendCooldown(60);

        // ✅ OPEN OTP SCREEN
        setStep("otp");

        toast.info(
          `Enter the OTP sent to ${
            res.data.maskedContact || maskPhone(normalised)
          }`,
        );

        return;
      }

      // ✅ Direct login fallback
      if (res.data?.token) {
        await finaliseLogin(res.data.token);
      }
    } catch (err) {
      console.log("Login error:", err?.response?.data);

      // ✅ HANDLE OTP RESPONSE INSIDE CATCH
      // Backend is likely returning 400/401 with otpRequired=true
      if (err?.response?.data?.otpRequired === true) {
        const masked = err.response.data.maskedContact || maskPhone(normalised);

        setMaskedContact(masked);

        setResendCooldown(60);

        // ✅ SHOW OTP PAGE INSTEAD OF LOGIN FAILED
        setStep("otp");

        return;
      }

      // ✅ ONLY SHOW ERROR FOR REAL FAILURES
      const responseData = err?.response?.data;

      const message =
        responseData?.message || "Login failed. Please try again.";

      // ✅ Disabled account
      if (responseData?.accountStatus === "disabled") {
        toast.error(message);
        setServerError(message);
        return;
      }

      // ✅ Deleted account
      if (responseData?.accountStatus === "deleted") {
        toast.error(message);
        setServerError(message);
        return;
      }

      // ✅ Normal errors
      // toast.error(message);
      setServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: Verify OTP ──
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otp = otpDigits.join("");
    if (otp.length !== 6) {
      setOtpError("Please enter the full 6-digit code");
      return;
    }
    setOtpError("");
    setIsLoading(true);

    try {
      const res = await api.post("/users/verify-login-otp", {
        identifier: pendingIdentifierRef.current,
        otp,
      });

      const { token } = res.data;
      await finaliseLogin(token);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Invalid or expired code.";
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Shared: fetch /me, call login(), redirect
  const finaliseLogin = async (token) => {
    localStorage.setItem("token", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const meResult = await api.get("/users/me");
    const freshUser = meResult.data.user;

    login(freshUser, token);
    toast.success(`Welcome back, ${freshUser.firstName}!`);

    setTimeout(() => {
      const redirectTo = consumePostAuthRedirect();
      if (redirectTo) return navigate(redirectTo);

      switch (freshUser.role) {
        case "student":
          return navigate("/student-home");
        case "tutor":
          return navigate("/tutor-home");
        case "institution":
          return navigate("/academy-home");
        // case "admin":
        //   return navigate("/admin/support");
        default:
          return navigate("/");
      }
    }, 800);
  };

  // ── Resend OTP ──
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setIsLoading(true);
    try {
      await api.post("/users/resend-login-otp", {
        identifier: pendingIdentifierRef.current,
      });
      setResendCooldown(60);
      setOtpDigits(["", "", "", "", "", ""]);
      setOtpError("");
      toast.info("A new code was sent.");
    } catch (err) {
      // toast.error("Failed to resend code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── OTP digit input helpers ──
  const otpRefs = useRef([]);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // digits only
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5)
      otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const next = [...otpDigits];
    pasted.split("").forEach((d, i) => {
      next[i] = d;
    });
    setOtpDigits(next);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  // ── Render ──
  return (
    <div className="container">
      {isLoading && <SpinnerOverlay />}

      {/* Logo */}
      <div className="logo-container">
        <img
          src="../images/tutors-logo.png"
          alt="Website Logo"
          className="logo"
        />
      </div>

      {/* ── STEP 1: Credentials ── */}
      {step === "credentials" && (
        <div className="login-form-container">
          <h1 className="title">Login</h1>

          {serverError && (
            <div className="server-error-banner">
              <p className="error">{serverError}</p>
            </div>
          )}

          <div className="form-section">
            <form onSubmit={handleSubmit}>
              {/* Identifier */}
              <div className="form-group">
                <label>Email or Phone Number</label>
                <input
                  type="text"
                  placeholder="tutorslinks@gmail.com  or  08012345678"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="input-field"
                />
                {/* Helper hint */}
                <small className="field-hint">
                  Phone: enter any format – 080…, 234…, or +234… all work.
                </small>
                {errors.identifier && (
                  <p className="error">{errors.identifier}</p>
                )}
              </div>

              {/* Password */}
              <div className="form-group">
                <label>Password</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="toggle-password"
                  >
                    <span className="eye-icon">👁️</span>
                  </button>
                </div>
                {errors.password && <p className="error">{errors.password}</p>}
                <a href="/reset-password" className="forgot-password">
                  Forgot password?
                </a>
              </div>

              <button type="submit" className="sign-in-btn">
                Login <span className="arrow">→</span>
              </button>
            </form>

            <hr className="separator" />
            <h3 className="continue-heading">Or Continue With</h3>
            <div className="social-login">
              <button
                type="button"
                className="social-btn"
                onClick={() => handleSocialAuth("google")}
              >
                <img
                  src="../images/google.png"
                  alt="Google"
                  className="social-icon"
                />
                <span className="social-text">Google</span>
              </button>
              <button
                type="button"
                className="social-btn"
                onClick={() => handleSocialAuth("facebook")}
              >
                <img
                  src="../images/facebook-logo.png"
                  alt="Facebook"
                  className="social-icon"
                />
                <span className="social-text">Facebook</span>
              </button>
              <button
                type="button"
                className="social-btn"
                onClick={() => handleSocialAuth("apple")}
              >
                <img
                  src="../images/apple.png"
                  alt="Apple"
                  className="social-icon"
                />
                <span className="social-text">Apple</span>
              </button>
            </div>

            <p className="signin-text">
              Don't have an account?{" "}
              <a href="/decision-screen" className="signin-link">
                Create One
              </a>
            </p>
          </div>
        </div>
      )}

      {/* ── STEP 2: OTP Verification ── */}
      {step === "otp" && (
        <div className="login-form-container">
          <h1 className="title">Verify It's You</h1>

          <div className="form-section otp-section">
            {/* Back link */}
            <button
              type="button"
              className="back-btn"
              onClick={() => {
                setStep("credentials");
                setOtpDigits(["", "", "", "", "", ""]);
                setOtpError("");
              }}
            >
              ← Back
            </button>

            <p className="otp-description">
              A 6-digit verification code was sent to{" "}
              <strong>{maskedContact}</strong>. Enter it below to sign in.
            </p>

            <form onSubmit={handleOtpSubmit}>
              {/* OTP digit boxes */}
              <div className="otp-input-row" onPaste={handleOtpPaste}>
                {otpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`otp-box ${digit ? "otp-box--filled" : ""}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              {otpError && <p className="error otp-error">{otpError}</p>}

              <button
                type="submit"
                className="sign-in-btn"
                disabled={otpDigits.join("").length !== 6}
              >
                Verify & Sign In <span className="arrow">→</span>
              </button>
            </form>

            {/* Resend */}
            <p className="login-resend-text">
              Didn't receive the code?{" "}
              {resendCooldown > 0 ? (
                <span className="resend-cooldown">
                  Resend in {resendCooldown}s
                </span>
              ) : (
                <button
                  type="button"
                  className="resend-btn"
                  onClick={handleResend}
                >
                  Resend Code
                </button>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Slideshow panel ── */}
      <div className="image-section">
        <div className="image-container">
          {images.map((src, index) => (
            <img
              key={index}
              src={src}
              alt={`Slide ${index + 1}`}
              className={index === currentImageIndex ? "active-image" : "image"}
            />
          ))}
          <div className="image-text">
            <h2>{imageTexts[currentImageIndex].title}</h2>
            <p>{imageTexts[currentImageIndex].subtitle}</p>
          </div>
          <div className="dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentImageIndex ? "active-dot" : ""}`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
